import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import NodeCrumbs from '../../shared/ProgramTree/NodeCrumbs';
import NodeTabs from '../../shared/ProgramTree/NodeTabs';
import { lessonPages, unitPages } from '../../shared/ProgramTree/contentPages';
import { useProgramSummary } from '../../shared/ProgramTree/usePrograms';
import SPADataTable from '../../shared/SPADataTable';
import SPAPageHeader from '../../shared/SPAPageHeader';
import { useSPAUser } from '../../shared/SPAUser';
import { useSPAToast } from '../../shared/SPAToast';
import {
  lessonGuidesPath, programLessonsPath, unitSlideShowsPath, useContentMount,
} from '../contentMount';
import ProgramNavRail from '../ProgramLessons/ProgramNavRail';
import AddLessonGuideMenu from './AddLessonGuideMenu';
import getLessonGuideColumns from './lessonGuideColumns';
import {
  LessonGuide, SlideShowParent, guideTitle, useLessonGuides, useReorderLessonGuides,
} from './useLessonGuides';

const guideRowId = (guide: LessonGuide) => guide.id;
// guideTitle, not the raw title: an untitled guide reads as "(untitled)" in the Title column, so
// the handle says the same thing instead of falling through to the generic "Reorder row".
const guideRowLabel = (guide: LessonGuide) => guideTitle(guide);

// AIDEV-NOTE: SPA recreation of the legacy lesson guides page (/admin/lessons/:id/slide_shows) and
// its unit twin (/admin/units/:id/slide_shows/unit_index), one page keyed by which route matched.
// The crumbs replace the legacy Unit/Lesson select: the whole program in a menu, units landing
// here for that unit and lessons landing here for that lesson. Rows drag to reorder, saved on drop.
const LessonGuidesPage = () => {
  const { lessonId, programId, unitId } = useParams<{ lessonId?: string; programId: string; unitId?: string }>();
  const pid = Number(programId);
  const parent = useMemo<SlideShowParent>(
    () => (unitId ? { id: Number(unitId), type: 'Unit' } : { id: Number(lessonId), type: 'Lesson' }),
    [lessonId, unitId],
  );
  const mount = useContentMount();
  const { showPrintTools } = useSPAUser();
  const spaToast = useSPAToast();
  const {
    color, isLoading: isLoadingTitle, science, title: programTitle,
  } = useProgramSummary(pid);
  const guides = useLessonGuides(parent);
  const reorderGuides = useReorderLessonGuides(pid, parent);
  const columns = useMemo(() => getLessonGuideColumns(pid, parent), [pid, parent]);
  const isUnit = parent.type === 'Unit';
  const noun = isUnit ? 'Slideshows' : 'Lesson Guides';

  const onReorder = (rows: LessonGuide[]) => reorderGuides(rows)
    .then(() => spaToast.success(`Saved ${noun.toLowerCase().replace(/s$/, '')} order.`));

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      {mount === 'spa' && (
        <ProgramNavRail
          isLoadingTitle={isLoadingTitle}
          lessonId={isUnit ? undefined : parent.id}
          programColor={color}
          programId={pid}
          programTitle={programTitle}
          unitId={isUnit ? parent.id : undefined}
        />
      )}
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        <NodeCrumbs
          lessonId={isUnit ? undefined : parent.id}
          programId={pid}
          programTitle={programTitle}
          toLesson={id => lessonGuidesPath(pid, id, mount)}
          toProgram={id => programLessonsPath(id, mount)}
          toUnit={id => unitSlideShowsPath(pid, id, mount)}
          unitId={isUnit ? parent.id : undefined}
        />
        <SPAPageHeader
          actions={(
            <AddLessonGuideMenu label={isUnit ? 'Add Slideshow' : 'Add Lesson Guide'} parent={parent} programId={pid} />
          )}
          title={noun}
        />
        <div className="tw-mb-5 tw-border-0 tw-border-b tw-border-solid tw-border-spa-border">
          <NodeTabs
            label={isUnit ? 'Unit pages' : 'Lesson pages'}
            pages={isUnit ? unitPages(parent.id, pid) : lessonPages(parent.id, pid, science, showPrintTools)}
          />
        </div>

        {guides.isError ? (
          <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">
            Couldn’t load the {noun.toLowerCase()}. Reload the page to try again.
          </p>
        ) : (
          <SPADataTable
            columns={columns}
            data={guides.data ?? []}
            defaultSort={[]}
            emptyMessage={`No ${noun.toLowerCase()} present for this ${isUnit ? 'unit' : 'lesson'}.`}
            isLoading={guides.isLoading}
            loadingRows={4}
            reorder={{ getRowId: guideRowId, getRowLabel: guideRowLabel, onReorder }}
          />
        )}
      </div>
    </div>
  );
};

export default LessonGuidesPage;
